import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { api } from '@/shared/api/axios';
import { CheckCircle, Upload, ChevronLeft, ChevronRight, Send, AlertCircle } from 'lucide-react';

interface CandidateData {
  firstName: string; lastName: string; email: string; phone: string; alternatePhone: string;
  dateOfBirth: string; gender: string; maritalStatus: string; bloodGroup: string; disability: string;
  aadharNumber: string; panNumber: string; uanNumber: string; esicNumber: string; drivingLicense: string;
  currentAddress: { street: string; city: string; state: string; pincode: string };
  permanentAddress: { street: string; city: string; state: string; pincode: string };
  bankName: string; accountNumber: string; ifscCode: string; branchName: string;
  family: { name: string; relation: string; contact: string }[];
  education: { schoolName: string; degree: string; fieldOfStudy: string; completionDate: string; additionalNotes: string }[];
  experience: { company: string; occupation: string; summary: string; duration: string; reasonForLeaving: string; currentlyWorkHere: boolean }[];
  isFresher: boolean; highestQualification: string; currentSalary: number; skillSet: string[];
  candidateDocuments: { code: string; label: string; required: boolean; applicableForFreshers: boolean; status: string; hasFile?: boolean; rejectionReason?: string }[];
  onboardingStatus: string; completionPercent: number; designation: string; department: string;
  candidateId: string; employeeId?: string; dateOfJoining: string;
}

const EMPTY: CandidateData = {
  firstName: '', lastName: '', email: '', phone: '', alternatePhone: '',
  dateOfBirth: '', gender: '', maritalStatus: '', bloodGroup: '', disability: 'None',
  aadharNumber: '', panNumber: '', uanNumber: '', esicNumber: '', drivingLicense: '',
  currentAddress: { street: '', city: '', state: '', pincode: '' },
  permanentAddress: { street: '', city: '', state: '', pincode: '' },
  bankName: '', accountNumber: '', ifscCode: '', branchName: '',
  family: [{ name: '', relation: 'father', contact: '' }, { name: '', relation: 'mother', contact: '' }],
  education: [{ schoolName: '', degree: '10th', fieldOfStudy: '', completionDate: '', additionalNotes: '' }, { schoolName: '', degree: '12th', fieldOfStudy: '', completionDate: '', additionalNotes: '' }, { schoolName: '', degree: 'Bachelors', fieldOfStudy: '', completionDate: '', additionalNotes: '' }],
  experience: [],
  isFresher: false, highestQualification: '', currentSalary: 0, skillSet: [],
  candidateDocuments: [], onboardingStatus: 'pending_info', completionPercent: 0,
  designation: '', department: '', candidateId: '', employeeId: '', dateOfJoining: '',
};

const STEPS = ['Personal Details', 'Address & Bank', 'Family Details', 'Education', 'Work Experience', 'Document Upload', 'Review & Submit'];

// ── Validation Logic ──────────────────────────────────────────────────────────

function validateStep(stepIndex: number, data: CandidateData): { valid: boolean; errors: Record<string, string>; message?: string } {
  const errs: Record<string, string> = {};

  if (stepIndex === 0) {
    if (!data.firstName?.trim()) errs.firstName = 'First name is required';
    if (!data.lastName?.trim()) errs.lastName = 'Last name is required';
    if (!data.email?.trim()) {
      errs.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email.trim())) {
      errs.email = 'Please enter a valid email address';
    }
    if (!data.phone?.trim()) {
      errs.phone = 'Phone number is required';
    } else if (data.phone.replace(/\D/g, '').length < 10) {
      errs.phone = 'Phone number must be at least 10 digits';
    }
    if (!data.dateOfBirth?.trim()) errs.dateOfBirth = 'Date of birth is required';
    if (!data.aadharNumber?.trim()) {
      errs.aadharNumber = 'Aadhar number is required';
    } else if (data.aadharNumber.replace(/[\s-]/g, '').length < 12) {
      errs.aadharNumber = 'Aadhar number must be 12 digits';
    }
    if (!data.panNumber?.trim()) {
      errs.panNumber = 'PAN number is required';
    } else if (data.panNumber.trim().length < 10) {
      errs.panNumber = 'PAN must be 10 characters';
    }

    if (Object.keys(errs).length > 0) {
      return { valid: false, errors: errs, message: 'Please fill in all required personal details before proceeding.' };
    }
  }

  if (stepIndex === 1) {
    // Current Address
    if (!data.currentAddress?.street?.trim()) errs.currentStreet = 'Street address is required';
    if (!data.currentAddress?.city?.trim()) errs.currentCity = 'City is required';
    if (!data.currentAddress?.state?.trim()) errs.currentState = 'State is required';
    if (!data.currentAddress?.pincode?.trim()) errs.currentPincode = 'Pin code is required';

    // Permanent Address
    if (!data.permanentAddress?.street?.trim()) errs.permStreet = 'Permanent street is required';
    if (!data.permanentAddress?.city?.trim()) errs.permCity = 'Permanent city is required';
    if (!data.permanentAddress?.state?.trim()) errs.permState = 'Permanent state is required';
    if (!data.permanentAddress?.pincode?.trim()) errs.permPincode = 'Permanent pin code is required';

    // Bank Details
    if (!data.bankName?.trim()) errs.bankName = 'Bank name is required';
    if (!data.accountNumber?.trim()) errs.accountNumber = 'Account number is required';
    if (!data.ifscCode?.trim()) errs.ifscCode = 'IFSC code is required';

    if (Object.keys(errs).length > 0) {
      return { valid: false, errors: errs, message: 'Please fill in all required address and bank details.' };
    }
  }

  if (stepIndex === 2) {
    // Family Details
    if (!data.family || data.family.length === 0) {
      return { valid: false, errors: { family: 'At least one family contact is required' }, message: 'Please add at least one family member as an emergency contact.' };
    }
    const hasValidMember = data.family.some(f => f.name?.trim() && f.contact?.trim());
    if (!hasValidMember) {
      return { valid: false, errors: { family: 'Name and contact are required' }, message: 'Please enter name and contact for at least one family contact.' };
    }
  }

  if (stepIndex === 3) {
    // Education Details
    if (!data.education || data.education.length === 0) {
      return { valid: false, errors: { education: 'At least one education record is required' }, message: 'Please enter your highest qualification or education details.' };
    }
    const hasValidEdu = data.education.some(e => e.schoolName?.trim() && e.degree?.trim());
    if (!hasValidEdu) {
      return { valid: false, errors: { education: 'School and degree required' }, message: 'Please provide school/college name and degree for at least one entry.' };
    }
  }

  if (stepIndex === 4) {
    // Work Experience
    if (!data.isFresher) {
      if (!data.experience || data.experience.length === 0) {
        return { valid: false, errors: { experience: 'Please add work experience or check "I am a fresher"' }, message: 'Please add your work experience or check "I am a fresher".' };
      }
      const hasValidExp = data.experience.some(e => e.company?.trim() && e.occupation?.trim());
      if (!hasValidExp) {
        return { valid: false, errors: { experience: 'Company name and role required' }, message: 'Please enter company name and job title, or check "I am a fresher".' };
      }
    }
  }

  if (stepIndex === 5) {
    // Document Upload: verify mandatory documents
    const missingDocs = (data.candidateDocuments || []).filter(
      d => d.required && d.status !== 'not_applicable' && d.status !== 'submitted' && d.status !== 'verified' && !d.hasFile
    );
    if (missingDocs.length > 0) {
      return {
        valid: false,
        errors: { documents: `Missing ${missingDocs.length} mandatory document(s)` },
        message: `Please upload all mandatory documents (${missingDocs.map(d => d.label).join(', ')}) before continuing.`,
      };
    }
  }

  return { valid: true, errors: {} };
}

// ── Main Component ────────────────────────────────────────────────────────────

export function EJoiningPage() {
  const { token } = useParams<{ token: string }>();
  const [data, setData] = useState<CandidateData>(EMPTY);
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [invalid, setInvalid] = useState(false);
  const [uploading, setUploading] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!token) return;
    api.get(`/api/onboarding-portal/portal/${token}`).then(r => {
      const c = r.data.candidate;
      setData({ ...EMPTY, ...c, dateOfBirth: c.dateOfBirth?.slice(0, 10) || '', dateOfJoining: c.dateOfJoining?.slice(0, 10) || '' });
      if (c.onboardingStatus !== 'pending_info') setSubmitted(true);
      setLoading(false);
    }).catch((err) => {
      if (err?.response?.status === 429) toast.error('Too many attempts. Please wait a few minutes and try again.');
      else toast.error('This link is invalid or has expired. Please ask HR for a new one.');
      setLoading(false);
      setInvalid(true);
    });
  }, [token]);

  const clearError = (k: string) => {
    setErrors(prev => {
      if (!prev[k]) return prev;
      const next = { ...prev };
      delete next[k];
      return next;
    });
  };

  const save = async (partial?: Partial<CandidateData>) => {
    if (!token) return;
    setSaving(true);
    try {
      const payload = partial || data;
      await api.put(`/api/onboarding-portal/portal/${token}`, payload);
    } catch { /* silent auto-save */ }
    setSaving(false);
  };

  const next = async () => {
    const res = validateStep(step, data);
    if (!res.valid) {
      setErrors(res.errors);
      toast.error(res.message || 'Please fill in all required fields before proceeding.');
      return;
    }
    setErrors({});
    await save();
    setStep(s => Math.min(s + 1, STEPS.length - 1));
  };

  const prev = () => {
    setErrors({});
    setStep(s => Math.max(s - 1, 0));
  };

  const handleStepClick = async (targetIndex: number) => {
    if (targetIndex <= step) {
      setErrors({});
      setStep(targetIndex);
      return;
    }
    // Validate each step in sequence before allowing forward jump
    for (let s = step; s < targetIndex; s++) {
      const res = validateStep(s, data);
      if (!res.valid) {
        setStep(s);
        setErrors(res.errors);
        toast.error(res.message || `Please complete all required fields in Step ${s + 1} (${STEPS[s]}) first.`);
        return;
      }
    }
    setErrors({});
    await save();
    setStep(targetIndex);
  };

  const handleSubmit = async () => {
    if (!token) return;
    // Validate all sections before final submission
    for (let s = 0; s < STEPS.length - 1; s++) {
      const res = validateStep(s, data);
      if (!res.valid) {
        setStep(s);
        setErrors(res.errors);
        toast.error(res.message || `Please complete all required fields in Step ${s + 1} (${STEPS[s]}).`);
        return;
      }
    }
    setErrors({});
    try {
      await save();
      await api.post(`/api/onboarding-portal/portal/${token}/submit`);
      setSubmitted(true);
      toast.success('Information submitted successfully! HR will review your documents.');
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Submission failed.');
    }
  };

  // Opens the file picker, then sends the chosen file (PDF, PNG or JPG, up to 8 MB).
  const uploadDoc = (code: string) => {
    if (!token) return;
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/pdf,image/png,image/jpeg,.pdf,.png,.jpg,.jpeg';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      if (file.size > 8 * 1024 * 1024) { toast.error('The file is larger than 8 MB.'); return; }
      const form = new FormData();
      form.append('file', file);
      setUploading(code);
      try {
        await api.post(`/api/onboarding-portal/portal/${token}/documents/${code}/upload`, form, { headers: { 'Content-Type': null } });
        setData(d => ({ ...d, candidateDocuments: d.candidateDocuments.map(doc => doc.code === code ? { ...doc, status: 'submitted', hasFile: true, rejectionReason: '' } : doc) }));
        clearError('documents');
        toast.success('Document uploaded.');
      } catch (err: any) { toast.error(err?.response?.data?.error || 'Upload failed.'); }
      setUploading(null);
    };
    input.click();
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-gray-50"><p className="text-lg">Loading E-Joining Portal...</p></div>;
  if (!token || invalid) return <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4"><p className="text-red-600 text-center">This link is invalid or has expired. Please ask HR for a new one.</p></div>;

  if (submitted) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md text-center">
        <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
        <h2 className="text-2xl font-bold mb-2">Information Submitted!</h2>
        <p className="text-gray-600 mb-4">Thank you, {data.firstName}. Your details have been received. HR will review and verify your documents.</p>
        <p className="text-sm text-gray-500">You can still upload missing documents using this link.</p>
        <div className="mt-6"><button onClick={() => { setSubmitted(false); setStep(5); }} className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm">Upload More Documents</button></div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-indigo-900 text-white py-6 px-4">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-2xl font-bold">ZYNX — E-Joining Portal</h1>
          <p className="text-indigo-200 text-sm mt-1">Welcome{data.firstName ? `, ${data.firstName}` : ''}! Please complete all sections below.</p>
          {data.candidateId && <p className="text-indigo-300 text-xs mt-1">Candidate ID: {data.candidateId}{data.employeeId ? ` · Employee ID: ${data.employeeId}` : ''} | {data.designation} — {data.department}</p>}
        </div>
      </div>

      {/* Progress bar */}
      <div className="max-w-3xl mx-auto px-4 mt-4">
        <div className="flex items-center gap-1 mb-2">
          {STEPS.map((s, i) => (
            <button
              key={i}
              onClick={() => handleStepClick(i)}
              className={`flex-1 h-2 rounded-full transition ${i <= step ? 'bg-indigo-600' : 'bg-gray-200'}`}
              title={`Step ${i + 1}: ${s}`}
            />
          ))}
        </div>
        <div className="flex justify-between text-xs text-gray-500">
          <span>Step {step + 1} of {STEPS.length}</span>
          <span className="font-medium text-gray-800">{STEPS[step]}</span>
          <span>{saving ? 'Saving...' : `${data.completionPercent || 0}% complete`}</span>
        </div>
      </div>

      {/* Form content */}
      <div className="max-w-3xl mx-auto px-4 py-6">
        <div className="bg-white rounded-xl shadow-sm border p-6 min-h-[400px]">
          {step === 0 && <StepPersonal data={data} setData={setData} errors={errors} clearError={clearError} />}
          {step === 1 && <StepAddressBank data={data} setData={setData} errors={errors} clearError={clearError} />}
          {step === 2 && <StepFamily data={data} setData={setData} errors={errors} clearError={clearError} />}
          {step === 3 && <StepEducation data={data} setData={setData} errors={errors} clearError={clearError} />}
          {step === 4 && <StepExperience data={data} setData={setData} errors={errors} clearError={clearError} />}
          {step === 5 && <StepDocuments data={data} uploadDoc={uploadDoc} uploading={uploading} errors={errors} />}
          {step === 6 && <StepReview data={data} />}
        </div>

        {/* Navigation buttons: ONLY ONE Submit button exists, in this navigation bar */}
        <div className="flex justify-between mt-4">
          <button
            onClick={prev}
            disabled={step === 0}
            className="flex items-center gap-1 px-4 py-2 rounded-lg border text-sm disabled:opacity-30 hover:bg-gray-50 font-medium"
          >
            <ChevronLeft className="h-4 w-4" />Previous
          </button>
          {step < STEPS.length - 1 ? (
            <button
              onClick={next}
              className="flex items-center gap-1 px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm hover:bg-indigo-700 font-medium shadow-sm transition"
            >
              Next<ChevronRight className="h-4 w-4" />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              className="flex items-center gap-1 px-5 py-2 rounded-lg bg-green-600 text-white text-sm hover:bg-green-700 font-medium shadow-sm transition"
            >
              <Send className="h-4 w-4" />Submit All Information
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ═══ Step Components ═════════════════════════════════════════════════════════

function Field({ label, children, required, error }: { label: string; children: React.ReactNode; required?: boolean; error?: string }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label}{required && <span className="text-red-500"> *</span>}
      </label>
      {children}
      {error && <p className="text-xs text-red-600 mt-1 font-medium">{error}</p>}
    </div>
  );
}

function Input({ value, onChange, error, className, ...props }: Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> & { value: string; onChange: (v: string) => void; error?: boolean; className?: string }) {
  return (
    <input
      className={`w-full border rounded-lg px-3 py-2 text-sm bg-white text-gray-900 outline-none transition ${
        error
          ? 'border-red-500 focus:ring-2 focus:ring-red-200 focus:border-red-500'
          : 'border-gray-300 focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400'
      } ${className || ''}`}
      value={value}
      onChange={e => onChange(e.target.value)}
      {...props}
    />
  );
}

function Sel({ value, onChange, options, error }: { value: string; onChange: (v: string) => void; options: { value: string; label: string }[]; error?: boolean }) {
  return (
    <select
      className={`w-full border rounded-lg px-3 py-2 text-sm bg-white text-gray-900 focus:ring-2 focus:ring-indigo-300 outline-none transition ${
        error ? 'border-red-500' : 'border-gray-300'
      }`}
      value={value}
      onChange={e => onChange(e.target.value)}
    >
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

function StepPersonal({ data, setData, errors, clearError }: { data: CandidateData; setData: (fn: (d: CandidateData) => CandidateData) => void; errors: Record<string, string>; clearError: (k: string) => void }) {
  const set = (k: keyof CandidateData, v: any) => {
    setData(d => ({ ...d, [k]: v }));
    clearError(k);
  };

  return (
    <div className="space-y-4">
      <div className="border-b pb-2 flex items-center justify-between">
        <h2 className="text-lg font-semibold">Personal Details</h2>
        <span className="text-xs text-red-500">* All marked fields are mandatory</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="First Name" required error={errors.firstName}>
          <Input error={!!errors.firstName} value={data.firstName} onChange={v => set('firstName', v)} placeholder="Enter first name" />
        </Field>
        <Field label="Last Name" required error={errors.lastName}>
          <Input error={!!errors.lastName} value={data.lastName} onChange={v => set('lastName', v)} placeholder="Enter last name" />
        </Field>
        <Field label="Email" required error={errors.email}>
          <Input error={!!errors.email} value={data.email} onChange={v => set('email', v)} type="email" placeholder="your@email.com" />
        </Field>
        <Field label="Phone" required error={errors.phone}>
          <Input error={!!errors.phone} value={data.phone} onChange={v => set('phone', v)} placeholder="10-digit mobile number" />
        </Field>
        <Field label="Alternate Phone">
          <Input value={data.alternatePhone} onChange={v => set('alternatePhone', v)} placeholder="Optional" />
        </Field>
        <Field label="Date of Birth" required error={errors.dateOfBirth}>
          <Input error={!!errors.dateOfBirth} value={data.dateOfBirth} onChange={v => set('dateOfBirth', v)} type="date" />
        </Field>
        <Field label="Gender">
          <Sel value={data.gender} onChange={v => set('gender', v)} options={[{value:'',label:'Select...'},{value:'male',label:'Male'},{value:'female',label:'Female'},{value:'other',label:'Other'}]} />
        </Field>
        <Field label="Marital Status">
          <Sel value={data.maritalStatus} onChange={v => set('maritalStatus', v)} options={[{value:'',label:'Select...'},{value:'single',label:'Single'},{value:'married',label:'Married'},{value:'divorced',label:'Divorced'},{value:'widowed',label:'Widowed'}]} />
        </Field>
        <Field label="Blood Group">
          <Input value={data.bloodGroup} onChange={v => set('bloodGroup', v)} placeholder="e.g. B+" />
        </Field>
        <Field label="Any Disability">
          <Input value={data.disability} onChange={v => set('disability', v)} placeholder="None" />
        </Field>
      </div>

      <h3 className="text-sm font-semibold mt-4 pt-2 border-t">Identity Numbers</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Aadhar Number" required error={errors.aadharNumber}>
          <Input error={!!errors.aadharNumber} value={data.aadharNumber} onChange={v => set('aadharNumber', v)} placeholder="12-digit Aadhar number" />
        </Field>
        <Field label="PAN Number" required error={errors.panNumber}>
          <Input error={!!errors.panNumber} value={data.panNumber} onChange={v => set('panNumber', v.toUpperCase())} placeholder="10-character PAN (e.g. ABCDE1234F)" />
        </Field>
        <Field label="UAN Number">
          <Input value={data.uanNumber} onChange={v => set('uanNumber', v)} placeholder="If available" />
        </Field>
        <Field label="ESIC Number">
          <Input value={data.esicNumber} onChange={v => set('esicNumber', v)} placeholder="If available" />
        </Field>
        <Field label="Driving License">
          <Input value={data.drivingLicense} onChange={v => set('drivingLicense', v)} placeholder="If applicable" />
        </Field>
      </div>
    </div>
  );
}

function StepAddressBank({ data, setData, errors, clearError }: { data: CandidateData; setData: (fn: (d: CandidateData) => CandidateData) => void; errors: Record<string, string>; clearError: (k: string) => void }) {
  const setAddr = (which: 'currentAddress' | 'permanentAddress', k: string, v: string) => {
    setData(d => ({ ...d, [which]: { ...d[which], [k]: v } }));
    clearError(which === 'currentAddress' ? `current${k[0].toUpperCase()}${k.slice(1)}` : `perm${k[0].toUpperCase()}${k.slice(1)}`);
  };
  const set = (k: keyof CandidateData, v: any) => {
    setData(d => ({ ...d, [k]: v }));
    clearError(k);
  };
  const [sameAddr, setSameAddr] = useState(false);

  return (
    <div className="space-y-4">
      <div className="border-b pb-2 flex items-center justify-between">
        <h2 className="text-lg font-semibold">Address & Bank Details</h2>
        <span className="text-xs text-red-500">* All marked fields are mandatory</span>
      </div>

      <h3 className="text-sm font-medium">Current Address</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="md:col-span-2">
          <Field label="Street Address" required error={errors.currentStreet}>
            <Input error={!!errors.currentStreet} value={data.currentAddress.street} onChange={v => setAddr('currentAddress', 'street', v)} placeholder="House / Flat no., Building, Street" />
          </Field>
        </div>
        <Field label="City" required error={errors.currentCity}>
          <Input error={!!errors.currentCity} value={data.currentAddress.city} onChange={v => setAddr('currentAddress', 'city', v)} placeholder="City" />
        </Field>
        <Field label="State" required error={errors.currentState}>
          <Input error={!!errors.currentState} value={data.currentAddress.state} onChange={v => setAddr('currentAddress', 'state', v)} placeholder="State" />
        </Field>
        <Field label="Pin Code" required error={errors.currentPincode}>
          <Input error={!!errors.currentPincode} value={data.currentAddress.pincode} onChange={v => setAddr('currentAddress', 'pincode', v)} placeholder="6-digit pin code" />
        </Field>
      </div>

      <div className="flex items-center gap-2 mt-2">
        <input
          type="checkbox"
          id="sameAddr"
          checked={sameAddr}
          onChange={e => {
            setSameAddr(e.target.checked);
            if (e.target.checked) {
              setData(d => ({ ...d, permanentAddress: { ...d.currentAddress } }));
              clearError('permStreet');
              clearError('permCity');
              clearError('permState');
              clearError('permPincode');
            }
          }}
          className="rounded text-indigo-600 cursor-pointer"
        />
        <label htmlFor="sameAddr" className="text-sm text-gray-700 cursor-pointer select-none">Permanent address is same as current</label>
      </div>

      {!sameAddr && (
        <>
          <h3 className="text-sm font-medium mt-3">Permanent Address</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <Field label="Permanent Street Address" required error={errors.permStreet}>
                <Input error={!!errors.permStreet} value={data.permanentAddress.street} onChange={v => setAddr('permanentAddress', 'street', v)} placeholder="House / Flat no., Building, Street" />
              </Field>
            </div>
            <Field label="City" required error={errors.permCity}>
              <Input error={!!errors.permCity} value={data.permanentAddress.city} onChange={v => setAddr('permanentAddress', 'city', v)} placeholder="City" />
            </Field>
            <Field label="State" required error={errors.permState}>
              <Input error={!!errors.permState} value={data.permanentAddress.state} onChange={v => setAddr('permanentAddress', 'state', v)} placeholder="State" />
            </Field>
            <Field label="Pin Code" required error={errors.permPincode}>
              <Input error={!!errors.permPincode} value={data.permanentAddress.pincode} onChange={v => setAddr('permanentAddress', 'pincode', v)} placeholder="6-digit pin code" />
            </Field>
          </div>
        </>
      )}

      <h2 className="text-lg font-semibold border-b pb-2 mt-6">Bank Account Details</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Bank Name" required error={errors.bankName}>
          <Input error={!!errors.bankName} value={data.bankName} onChange={v => set('bankName', v)} placeholder="e.g. HDFC Bank, ICICI Bank" />
        </Field>
        <Field label="Account Number" required error={errors.accountNumber}>
          <Input error={!!errors.accountNumber} value={data.accountNumber} onChange={v => set('accountNumber', v)} placeholder="Enter full bank account number" />
        </Field>
        <Field label="IFSC Code" required error={errors.ifscCode}>
          <Input error={!!errors.ifscCode} value={data.ifscCode} onChange={v => set('ifscCode', v.toUpperCase())} placeholder="e.g. HDFC0001234" />
        </Field>
        <Field label="Branch Name">
          <Input value={data.branchName} onChange={v => set('branchName', v)} placeholder="e.g. Pune Camp" />
        </Field>
      </div>
    </div>
  );
}

function StepFamily({ data, setData, errors, clearError }: { data: CandidateData; setData: (fn: (d: CandidateData) => CandidateData) => void; errors: Record<string, string>; clearError: (k: string) => void }) {
  const addRow = () => setData(d => ({ ...d, family: [...d.family, { name: '', relation: 'other', contact: '' }] }));
  const removeRow = (i: number) => setData(d => ({ ...d, family: d.family.filter((_, idx) => idx !== i) }));
  const updateRow = (i: number, k: string, v: string) => {
    setData(d => ({ ...d, family: d.family.map((f, idx) => idx === i ? { ...f, [k]: v } : f) }));
    clearError('family');
  };

  return (
    <div className="space-y-4">
      <div className="border-b pb-2 flex items-center justify-between">
        <h2 className="text-lg font-semibold">Family Details</h2>
        <span className="text-xs text-red-500">* Emergency contact details</span>
      </div>
      <p className="text-sm text-gray-500">Please provide family contact details in case of emergencies.</p>
      {errors.family && (
        <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-xs text-red-700 flex items-center gap-1.5">
          <AlertCircle className="h-4 w-4 shrink-0 text-red-500" />
          <span>{errors.family}</span>
        </div>
      )}
      <div className="space-y-3">
        {data.family.map((f, i) => (
          <div key={i} className="grid grid-cols-[1fr_140px_1fr_auto] gap-2 items-end">
            <Field label={i === 0 ? 'Full Name *' : ''}>
              <Input value={f.name} onChange={v => updateRow(i, 'name', v)} placeholder="Full name" />
            </Field>
            <Field label={i === 0 ? 'Relation' : ''}>
              <Sel value={f.relation} onChange={v => updateRow(i, 'relation', v)} options={[{value:'father',label:'Father'},{value:'mother',label:'Mother'},{value:'spouse',label:'Spouse'},{value:'brother',label:'Brother'},{value:'sister',label:'Sister'},{value:'child',label:'Child'},{value:'other',label:'Other'}]} />
            </Field>
            <Field label={i === 0 ? 'Contact Phone *' : ''}>
              <Input value={f.contact} onChange={v => updateRow(i, 'contact', v)} placeholder="10-digit mobile" />
            </Field>
            <button onClick={() => removeRow(i)} className="p-2 text-red-500 hover:bg-red-50 rounded" title="Remove">✕</button>
          </div>
        ))}
      </div>
      {data.family.length < 7 && (
        <button onClick={addRow} className="text-sm font-medium text-indigo-600 hover:underline">+ Add family member</button>
      )}
    </div>
  );
}

function StepEducation({ data, setData, errors, clearError }: { data: CandidateData; setData: (fn: (d: CandidateData) => CandidateData) => void; errors: Record<string, string>; clearError: (k: string) => void }) {
  const addRow = () => setData(d => ({ ...d, education: [...d.education, { schoolName: '', degree: '', fieldOfStudy: '', completionDate: '', additionalNotes: '' }] }));
  const removeRow = (i: number) => setData(d => ({ ...d, education: d.education.filter((_, idx) => idx !== i) }));
  const updateRow = (i: number, k: string, v: string) => {
    setData(d => ({ ...d, education: d.education.map((e, idx) => idx === i ? { ...e, [k]: v } : e) }));
    clearError('education');
  };

  return (
    <div className="space-y-4">
      <div className="border-b pb-2 flex items-center justify-between">
        <h2 className="text-lg font-semibold">Education Qualifications</h2>
        <span className="text-xs text-red-500">* At least one qualification entry required</span>
      </div>
      {errors.education && (
        <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-xs text-red-700 flex items-center gap-1.5">
          <AlertCircle className="h-4 w-4 shrink-0 text-red-500" />
          <span>{errors.education}</span>
        </div>
      )}
      <div className="space-y-3">
        {data.education.map((e, i) => (
          <div key={i} className="p-3 border rounded-lg bg-gray-50 space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-xs font-medium text-gray-500">Qualification Entry #{i + 1}</span>
              {data.education.length > 1 && <button onClick={() => removeRow(i)} className="text-xs text-red-500 hover:underline">Remove</button>}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              <Input value={e.schoolName} onChange={v => updateRow(i, 'schoolName', v)} placeholder="School / College / Institute name *" />
              <Input value={e.degree} onChange={v => updateRow(i, 'degree', v)} placeholder="Degree (10th, 12th, B.Tech, MBA...) *" />
              <Input value={e.fieldOfStudy} onChange={v => updateRow(i, 'fieldOfStudy', v)} placeholder="Field of study (e.g. Computer Science)" />
              <Input value={e.completionDate?.slice(0, 10) || ''} onChange={v => updateRow(i, 'completionDate', v)} type="date" />
            </div>
          </div>
        ))}
      </div>
      <button onClick={addRow} className="text-sm font-medium text-indigo-600 hover:underline">+ Add another qualification</button>
    </div>
  );
}

function StepExperience({ data, setData, errors, clearError }: { data: CandidateData; setData: (fn: (d: CandidateData) => CandidateData) => void; errors: Record<string, string>; clearError: (k: string) => void }) {
  const set = (k: keyof CandidateData, v: any) => {
    setData(d => ({ ...d, [k]: v }));
    clearError(k);
  };
  const addRow = () => setData(d => ({ ...d, experience: [...d.experience, { company: '', occupation: '', summary: '', duration: '', reasonForLeaving: '', currentlyWorkHere: false }] }));
  const removeRow = (i: number) => setData(d => ({ ...d, experience: d.experience.filter((_, idx) => idx !== i) }));
  const updateRow = (i: number, k: string, v: any) => {
    setData(d => ({ ...d, experience: d.experience.map((e, idx) => idx === i ? { ...e, [k]: v } : e) }));
    clearError('experience');
  };

  return (
    <div className="space-y-4">
      <div className="border-b pb-2 flex items-center justify-between">
        <h2 className="text-lg font-semibold">Work Experience</h2>
        <span className="text-xs text-gray-500">Provide previous employment history or mark as Fresher</span>
      </div>

      <div className="flex items-center gap-2 p-3 border rounded-lg bg-blue-50">
        <input
          type="checkbox"
          id="isFresherCheck"
          checked={data.isFresher}
          onChange={e => {
            set('isFresher', e.target.checked);
            clearError('experience');
          }}
          className="rounded text-indigo-600 cursor-pointer"
        />
        <label htmlFor="isFresherCheck" className="text-sm font-medium text-gray-800 cursor-pointer select-none">
          I am a fresher (no previous work experience)
        </label>
      </div>

      {errors.experience && (
        <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-xs text-red-700 flex items-center gap-1.5">
          <AlertCircle className="h-4 w-4 shrink-0 text-red-500" />
          <span>{errors.experience}</span>
        </div>
      )}

      {!data.isFresher && (
        <>
          <div className="space-y-3">
            {data.experience.map((e, i) => (
              <div key={i} className="p-3 border rounded-lg bg-gray-50 space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-xs font-medium text-gray-500">Company #{i + 1}</span>
                  <button onClick={() => removeRow(i)} className="text-xs text-red-500 hover:underline">Remove</button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <Input value={e.company} onChange={v => updateRow(i, 'company', v)} placeholder="Company name *" />
                  <Input value={e.occupation} onChange={v => updateRow(i, 'occupation', v)} placeholder="Job title / role *" />
                  <Input value={e.duration} onChange={v => updateRow(i, 'duration', v)} placeholder="Duration (e.g. 2 years)" />
                  <Input value={e.reasonForLeaving} onChange={v => updateRow(i, 'reasonForLeaving', v)} placeholder="Reason for leaving" />
                </div>
              </div>
            ))}
          </div>
          <button onClick={addRow} className="text-sm font-medium text-indigo-600 hover:underline">+ Add work experience entry</button>
        </>
      )}

      <h3 className="text-sm font-semibold mt-4 pt-2 border-t">Additional Details</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field label="Highest Qualification">
          <Input value={data.highestQualification} onChange={v => set('highestQualification', v)} placeholder="e.g. MBA, B.Tech, MCA" />
        </Field>
        <Field label="Current / Last Annual CTC (₹)">
          <Input value={String(data.currentSalary || '')} onChange={v => set('currentSalary', Number(v) || 0)} type="number" placeholder="Annual salary in INR" />
        </Field>
      </div>
    </div>
  );
}

function StepDocuments({ data, uploadDoc, uploading, errors }: { data: CandidateData; uploadDoc: (code: string) => void; uploading: string | null; errors: Record<string, string> }) {
  const docs = data.candidateDocuments || [];
  const statusIcon = (s: string) => {
    if (s === 'submitted' || s === 'verified') return <CheckCircle className="h-4 w-4 text-green-500" />;
    if (s === 'not_applicable') return <span className="text-xs text-gray-400">N/A</span>;
    if (s === 'rejected') return <span className="text-xs text-red-500 font-medium">Rejected</span>;
    return <span className="text-xs text-orange-500">Pending</span>;
  };

  return (
    <div className="space-y-4">
      <div className="border-b pb-2 flex items-center justify-between">
        <h2 className="text-lg font-semibold">Document Upload</h2>
        <span className="text-xs text-red-500">* All marked documents are mandatory</span>
      </div>

      <p className="text-sm text-gray-500">Upload all required documents. Supported formats: PDF, PNG, JPG (up to 8 MB).</p>

      {errors.documents && (
        <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-xs text-red-700 flex items-center gap-1.5">
          <AlertCircle className="h-4 w-4 shrink-0 text-red-500" />
          <span>{errors.documents}</span>
        </div>
      )}

      <div className="space-y-2">
        {docs.map(doc => (
          <div key={doc.code} className={`flex items-center justify-between p-3 border rounded-lg ${doc.status === 'not_applicable' ? 'bg-gray-50 opacity-60' : 'bg-white'}`}>
            <div className="flex items-center gap-3">
              {statusIcon(doc.status)}
              <div>
                <span className="text-sm font-medium text-gray-800">
                  {doc.label}
                  {doc.required && doc.status !== 'not_applicable' ? <span className="text-red-500 font-bold"> *</span> : ''}
                </span>
                {doc.status === 'rejected' && doc.rejectionReason && (
                  <p className="text-xs text-red-600 mt-0.5">HR feedback: {doc.rejectionReason}</p>
                )}
              </div>
            </div>
            {doc.status === 'not_applicable' ? (
              <span className="text-xs text-gray-400 italic">Not Applicable</span>
            ) : doc.status === 'pending' || doc.status === 'rejected' ? (
              <button
                onClick={() => uploadDoc(doc.code)}
                disabled={uploading === doc.code}
                className="flex items-center gap-1 px-3 py-1.5 text-xs bg-indigo-50 text-indigo-700 rounded-lg hover:bg-indigo-100 font-medium disabled:opacity-60 transition"
              >
                <Upload className="h-3.5 w-3.5" />
                {uploading === doc.code ? 'Uploading...' : 'Upload'}
              </button>
            ) : (
              <span className="text-xs text-green-600 font-medium bg-green-50 px-2 py-1 rounded border border-green-200">Uploaded ✓</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function StepReview({ data }: { data: CandidateData }) {
  return (
    <div className="space-y-4">
      <h2 className="text-lg font-semibold border-b pb-2">Review & Submit</h2>
      <p className="text-sm text-gray-500">Please review your information before final submission.</p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2.5 text-sm bg-gray-50/70 p-4 rounded-lg border">
        <div><span className="text-gray-500 text-xs block">Full Name</span> <strong className="text-gray-900">{data.firstName} {data.lastName}</strong></div>
        <div><span className="text-gray-500 text-xs block">Email Address</span> <span className="text-gray-900">{data.email || '—'}</span></div>
        <div><span className="text-gray-500 text-xs block">Phone Number</span> <span className="text-gray-900">{data.phone || '—'}</span></div>
        <div><span className="text-gray-500 text-xs block">Date of Birth</span> <span className="text-gray-900">{data.dateOfBirth || '—'}</span></div>
        <div><span className="text-gray-500 text-xs block">Gender</span> <span className="text-gray-900 capitalize">{data.gender || '—'}</span></div>
        <div><span className="text-gray-500 text-xs block">Blood Group</span> <span className="text-gray-900">{data.bloodGroup || '—'}</span></div>
        <div><span className="text-gray-500 text-xs block">Current City</span> <span className="text-gray-900">{data.currentAddress?.city || '—'}</span></div>
        <div><span className="text-gray-500 text-xs block">Bank Name</span> <span className="text-gray-900">{data.bankName || '—'}</span></div>
        <div><span className="text-gray-500 text-xs block">Account Number</span> <span className="text-gray-900">{data.accountNumber ? '•••• ' + data.accountNumber.slice(-4) : '—'}</span></div>
        <div><span className="text-gray-500 text-xs block">Candidate Status</span> <span className="text-gray-900">{data.isFresher ? 'Fresher' : 'Experienced'}</span></div>
      </div>

      <div className="mt-4 p-3.5 border rounded-lg bg-amber-50/80 border-amber-200 text-xs text-amber-900 leading-relaxed">
        <strong>Important Note:</strong> After you submit, HR will verify your documents and profile details. If additional documents are requested, you can re-access this link at any time to upload updated files.
      </div>
    </div>
  );
}
